/**
 * JobList — List of jobs within a selected pipeline.
 *
 * Shows job name, target table, layer badge (color from LAYER_COLORS by
 * hash index — not hardcoded to bronze/silver/gold), and load strategy chip.
 * Per-job run status/progress now lives in the top-level "Pipeline Runs"
 * tab (PipelineRunsTab, via PipelineRunsDashboard) — this list is job
 * management only (create/edit/delete/run-all), so switching tabs doesn't
 * interrupt an in-progress run poll and vice versa.
 *
 * Hover-revealed Edit and Delete (soft-delete with confirmation) action
 * buttons per row (§15.1 hover-revealed pattern).
 *
 * Empty state via shared <EmptyState> component (NFR30).
 * Clicking a row opens the JobEditor for that job.
 */

import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  Pencil,
  Trash2,
  Plus,
  Loader2,
  Wand2,
  Sparkles,
  PlayCircle,
  ListX,
} from "lucide-react";
import { EmptyState } from "@/shared/components/platform/EmptyState";
import type {
  PEJob,
  JobCreatePayload,
  LoadStrategy,
} from "../../features/pipeline-engineer/types";
import {
  listJobs,
  createJob,
  deleteJob,
  runAllJobs,
  deleteAllJobs,
} from "../../features/pipeline-engineer/api/pe-client";
import { IntrospectWizard } from "./IntrospectWizard";

// ---------------------------------------------------------------------------
// Design system constants
// ---------------------------------------------------------------------------

/**
 * Layer badge colours — assigned by hash index of the layer string so any
 * free-form target_layer value gets a stable, readable colour (NFR27).
 */
const LAYER_COLORS = [
  "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  "bg-purple-500/10 text-purple-400 border-purple-500/20",
  "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  "bg-orange-500/10 text-orange-400 border-orange-500/20",
  "bg-pink-500/10 text-pink-400 border-pink-500/20",
  "bg-blue-500/10 text-blue-400 border-blue-500/20",
  "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  "bg-red-500/10 text-red-400 border-red-500/20",
  "bg-teal-500/10 text-teal-400 border-teal-500/20",
  "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
] as const;

function layerColorClass(layer: string): string {
  let hash = 0;
  for (let i = 0; i < layer.length; i++) {
    hash = (hash * 31 + layer.charCodeAt(i)) | 0;
  }
  return LAYER_COLORS[Math.abs(hash) % LAYER_COLORS.length];
}

const STRATEGY_CHIP: Record<LoadStrategy, string> = {
  full_replace: "bg-sky-500/10 text-sky-400 border border-sky-500/20",
  incremental_merge:
    "bg-green-500/10 text-green-400 border border-green-500/20",
  scd1: "bg-violet-500/10 text-violet-400 border border-violet-500/20",
  scd2: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
};

const STRATEGY_LABEL: Record<LoadStrategy, string> = {
  full_replace: "Full Replace",
  incremental_merge: "Incremental",
  scd1: "SCD1",
  scd2: "SCD2",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface JobListProps {
  pipelineId: string;
  /** Project ID — passed to IntrospectWizard for LLM provider resolution. */
  projectId: string;
  refreshKey?: number;
  selectedJobId?: string;
  onSelectJob: (job: PEJob) => void;
  onJobCreated: (job: PEJob) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JobList({
  pipelineId,
  projectId,
  refreshKey = 0,
  selectedJobId,
  onSelectJob,
  onJobCreated,
}: JobListProps) {
  const [jobs, setJobs] = useState<PEJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [runAllPending, setRunAllPending] = useState(false);
  const [runAllMsg, setRunAllMsg] = useState<string | null>(null);
  const [runAllError, setRunAllError] = useState<string | null>(null);
  const [deleteAllPending, setDeleteAllPending] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listJobs(pipelineId);
      setJobs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs, refreshKey, localRefresh]);

  const handleDelete = useCallback(
    async (job: PEJob) => {
      if (
        !window.confirm(`Delete job "${job.jobName}"? This cannot be undone.`)
      ) {
        return;
      }
      setDeleteError(null);
      try {
        await deleteJob(pipelineId, job.id);
        setLocalRefresh((k) => k + 1);
      } catch (err) {
        setDeleteError(
          err instanceof Error ? err.message : "Failed to delete job.",
        );
      }
    },
    [pipelineId],
  );

  const handleJobCreated = useCallback(
    (job: PEJob) => {
      setShowCreate(false);
      setLocalRefresh((k) => k + 1);
      onJobCreated(job);
    },
    [onJobCreated],
  );

  const handleRunAll = useCallback(async () => {
    if (
      !window.confirm(
        "Run all jobs in this pipeline? Jobs using Full Replace will replace all rows in their target tables.",
      )
    ) {
      return;
    }
    setRunAllError(null);
    setRunAllMsg(null);
    setRunAllPending(true);
    try {
      // Run mode (parallel/sequential/custom order) is configured per
      // pipeline from the Scheduler tab; ad hoc Run All always runs in
      // parallel.
      const result = await runAllJobs(pipelineId, "parallel");
      setRunAllMsg(
        `${result.jobsQueued} job${result.jobsQueued !== 1 ? "s" : ""} queued.`,
      );
    } catch (err) {
      setRunAllError(
        err instanceof Error ? err.message : "Failed to run all jobs.",
      );
    } finally {
      setRunAllPending(false);
    }
  }, [pipelineId]);

  const handleDeleteAll = useCallback(async () => {
    if (jobs.length === 0) return;
    if (
      !window.confirm(
        `Delete all ${jobs.length} job${jobs.length !== 1 ? "s" : ""} in this pipeline? This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeleteError(null);
    setDeleteAllPending(true);
    try {
      await deleteAllJobs(pipelineId);
      setLocalRefresh((k) => k + 1);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Failed to delete all jobs.",
      );
    } finally {
      setDeleteAllPending(false);
    }
  }, [pipelineId, jobs.length]);

  /** Called after wizard's Accept All — refresh the job list. */
  const handleWizardJobsAccepted = useCallback((_jobsCreated: number) => {
    setShowWizard(false);
    setLocalRefresh((k) => k + 1);
  }, []);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-4 space-y-3">
        <JobListHeader
          onNew={() => setShowCreate(true)}
          onAIAssist={() => setShowWizard(true)}
          onRunAll={() => void handleRunAll()}
          runAllPending={runAllPending}
          onDeleteAll={() => void handleDeleteAll()}
          deleteAllPending={deleteAllPending}
          jobCount={0}
        />
        <div className="flex items-center justify-center py-10">
          <Loader2 size={20} className="animate-spin text-white/30" />
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error !== null) {
    return (
      <div className="glass-card rounded-2xl p-4 space-y-3">
        <JobListHeader
          onNew={() => setShowCreate(true)}
          onAIAssist={() => setShowWizard(true)}
          onRunAll={() => void handleRunAll()}
          runAllPending={runAllPending}
          onDeleteAll={() => void handleDeleteAll()}
          deleteAllPending={deleteAllPending}
          jobCount={0}
        />
        <EmptyState
          variant="system-issue"
          heading="Failed to load jobs"
          description={error}
          primaryAction={{ label: "Retry", onClick: () => void fetchJobs() }}
        />
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-4 space-y-3">
      <JobListHeader
        onNew={() => setShowCreate(true)}
        onAIAssist={() => setShowWizard(true)}
        onRunAll={() => void handleRunAll()}
        runAllPending={runAllPending}
        onDeleteAll={() => void handleDeleteAll()}
        deleteAllPending={deleteAllPending}
        jobCount={jobs.length}
      />

      {runAllMsg !== null && (
        <p className="text-emerald-400 text-xs px-1">{runAllMsg}</p>
      )}
      {runAllError !== null && (
        <p className="text-red-400 text-xs px-1" role="alert">
          {runAllError}
        </p>
      )}

      {deleteError !== null && (
        <p className="text-red-400 text-xs px-1" role="alert">
          {deleteError}
        </p>
      )}

      {/* AI Assist Wizard — shown inline above the job list */}
      {showWizard && (
        <IntrospectWizard
          pipelineId={pipelineId}
          projectId={projectId}
          onJobsAccepted={handleWizardJobsAccepted}
          onClose={() => setShowWizard(false)}
        />
      )}

      {showCreate && (
        <JobCreateForm
          pipelineId={pipelineId}
          onCreated={handleJobCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {jobs.length === 0 && !showCreate ? (
        <EmptyState
          variant="first-visit"
          heading="No jobs yet"
          description="Add a job to define the mapping sheet for a target table."
          primaryAction={{
            label: "New Job",
            onClick: () => setShowCreate(true),
          }}
        />
      ) : (
        <ul
          className="space-y-1"
          role="listbox"
          aria-label="Job list"
          aria-multiselectable="false"
        >
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              isSelected={job.id === selectedJobId}
              onSelect={onSelectJob}
              onEdit={onSelectJob}
              onDelete={(j) => void handleDelete(j)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function JobListHeader({
  onNew,
  onAIAssist,
  onRunAll,
  runAllPending,
  onDeleteAll,
  deleteAllPending,
  jobCount,
}: {
  onNew: () => void;
  onAIAssist: () => void;
  onRunAll: () => void;
  runAllPending: boolean;
  onDeleteAll: () => void;
  deleteAllPending: boolean;
  jobCount: number;
}) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white/80">Jobs</h2>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRunAll}
          disabled={runAllPending || jobCount === 0}
          aria-label="Run all jobs in this pipeline"
          title="Run all jobs in this pipeline"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {runAllPending ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <PlayCircle size={13} aria-hidden="true" />
          )}
          Run All
        </button>
        <button
          type="button"
          onClick={onDeleteAll}
          disabled={deleteAllPending || jobCount === 0}
          aria-label="Delete all jobs in this pipeline"
          title="Delete all jobs in this pipeline"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {deleteAllPending ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <ListX size={13} aria-hidden="true" />
          )}
          Delete All
        </button>
        <button
          type="button"
          onClick={onAIAssist}
          aria-label="Create pipeline mapping — AI, upload, or reuse from Data Converter"
          title="Create a pipeline mapping — AI propose, upload, or reuse from Data Converter"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500/20 hover:bg-brand-500/30 text-brand-300 border border-brand-500/30 rounded-lg text-xs font-medium transition-colors"
        >
          <Sparkles size={13} aria-hidden="true" />
          Create Pipeline Mapping
        </button>
        <button
          type="button"
          onClick={onNew}
          aria-label="Create new job"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors"
        >
          <Plus size={13} aria-hidden="true" />
          New Job
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job row
// ---------------------------------------------------------------------------

interface JobRowProps {
  job: PEJob;
  isSelected: boolean;
  onSelect: (job: PEJob) => void;
  onEdit: (job: PEJob) => void;
  onDelete: (job: PEJob) => void;
}

function JobRow({ job, isSelected, onSelect, onEdit, onDelete }: JobRowProps) {
  const layerClass = layerColorClass(job.targetLayer);
  const strategyClass =
    STRATEGY_CHIP[job.loadStrategy] ??
    "bg-white/5 text-white/50 border border-white/10";

  return (
    <li
      className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
        isSelected
          ? "bg-brand-500/15 border border-brand-500/30"
          : "hover:bg-white/5 border border-transparent"
      }`}
      role="option"
      aria-selected={isSelected}
      onClick={() => onSelect(job)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(job);
        }
      }}
      tabIndex={0}
    >
      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white truncate">
            {job.jobName}
          </span>
          {/* Layer badge — dynamic color by hash (NFR27) */}
          <span
            className={`border px-2 py-0.5 rounded-full text-xs font-medium ${layerClass}`}
          >
            {job.targetLayer}
          </span>
          {/* Load strategy chip */}
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${strategyClass}`}
          >
            {STRATEGY_LABEL[job.loadStrategy] ?? job.loadStrategy}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-white/40 truncate">
            {job.targetTable}
          </span>
        </div>
      </div>

      {/* Hover-revealed actions */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        <button
          type="button"
          aria-label={`Edit job ${job.jobName}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(job);
          }}
          className="p-1.5 text-white/40 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
        >
          <Pencil size={13} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`Delete job ${job.jobName}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(job);
          }}
          className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
        >
          <Trash2 size={13} aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Inline job create form
// ---------------------------------------------------------------------------

const LOAD_STRATEGIES: Array<{ value: LoadStrategy; label: string }> = [
  { value: "full_replace", label: "Full Replace" },
  { value: "incremental_merge", label: "Incremental Merge" },
  { value: "scd1", label: "SCD1 (Overwrite)" },
  { value: "scd2", label: "SCD2 (Historical)" },
];

interface JobCreateFormProps {
  pipelineId: string;
  onCreated: (job: PEJob) => void;
  onCancel: () => void;
}

function JobCreateForm({
  pipelineId,
  onCreated,
  onCancel,
}: JobCreateFormProps) {
  const [jobName, setJobName] = useState("");
  const [targetLayer, setTargetLayer] = useState("");
  const [loadStrategy, setLoadStrategy] =
    useState<LoadStrategy>("full_replace");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setFormError(null);
      setSubmitting(true);
      try {
        // Target table is picked later, in the job editor's Target tab
        // (select-existing-table, or leave unset to auto-create on run) — the
        // job name is a safe placeholder identifier until then. Primary keys
        // are also configured later, in the job editor's Source tab.
        const payload: JobCreatePayload = {
          job_name: jobName.trim(),
          target_layer: targetLayer.trim(),
          target_table: jobName.trim(),
          load_strategy: loadStrategy,
          primary_keys: [],
        };
        const job = await createJob(pipelineId, payload);
        onCreated(job);
      } catch (err) {
        setFormError(
          err instanceof Error ? err.message : "Failed to create job.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [jobName, targetLayer, loadStrategy, pipelineId, onCreated],
  );

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3"
    >
      <h3 className="text-xs font-semibold text-white/70">New Job</h3>

      <div>
        <label htmlFor="jc-job-name" className={LABEL_CLASS}>
          Job Name{" "}
          <span aria-hidden="true" className="text-red-400">
            *
          </span>
        </label>
        <input
          id="jc-job-name"
          type="text"
          required
          className={INPUT_CLASS}
          placeholder="e.g. bronze_orders"
          value={jobName}
          onChange={(e) => setJobName(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="jc-target-layer" className={LABEL_CLASS}>
          Layer{" "}
          <span aria-hidden="true" className="text-red-400">
            *
          </span>
        </label>
        <input
          id="jc-target-layer"
          type="text"
          required
          className={INPUT_CLASS}
          placeholder="e.g. bronze"
          value={targetLayer}
          onChange={(e) => setTargetLayer(e.target.value)}
        />
      </div>
      <p className="text-xs text-white/40">
        You&apos;ll select or create the target table (Target tab) and primary
        keys (Source tab) in the job editor, after the job is created.
      </p>

      <div>
        <label htmlFor="jc-load-strategy" className={LABEL_CLASS}>
          Load Strategy
        </label>
        <select
          id="jc-load-strategy"
          className={INPUT_CLASS}
          value={loadStrategy}
          onChange={(e) => setLoadStrategy(e.target.value as LoadStrategy)}
        >
          {LOAD_STRATEGIES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {formError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {formError}
        </p>
      )}

      <div className="flex gap-2 justify-end pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <Wand2 size={12} aria-hidden="true" />
          )}
          Create Job
        </button>
      </div>
    </form>
  );
}
